import React, { useState, useRef, useEffect } from 'react';
import { FaEllipsisH, FaBan, FaFlag } from 'react-icons/fa';
import { useBlockStatus } from '../hooks/useBlockStatus';
import { useAuth } from '../contexts/authContext';
import './UserSafetyMenu.css';

/**
 * Reusable ellipsis menu for Block/Report actions on user profiles
 * Can be used in ProfileHeader, hover cards, etc.
 */
const UserSafetyMenu = ({ targetUserId, targetDisplayName, onBlockChange }) => {
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  
  const {
    blocked,
    blockedBy,
    toggleBlock,
    loading: blockLoading,
  } = useBlockStatus(user?.id, targetUserId);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  // Don't show menu for own profile
  if (!user?.id || !targetUserId || user.id === targetUserId) {
    return null;
  }

  const handleBlock = async () => {
    const result = await toggleBlock();
    if (result.success && onBlockChange) {
      onBlockChange(!blocked);
    }
    setShowMenu(false);
  };

  const handleReport = () => {
    window.dispatchEvent(
      new CustomEvent('virtulingu:openReportModal', {
        detail: {
          userId: targetUserId,
          displayName: targetDisplayName,
          context: 'profile',
        },
      })
    );
    setShowMenu(false);
  };

  return (
    <div className="user-safety-menu" ref={menuRef}>
      <button
        className="safety-menu-trigger"
        onClick={() => setShowMenu((v) => !v)}
        title="More actions"
        aria-haspopup="true"
        aria-expanded={showMenu}
      >
        <FaEllipsisH />
      </button>

      {showMenu && (
        <div className="safety-menu-dropdown">
          <button className="safety-menu-item" onClick={handleReport}>
            <FaFlag />
            <span>Report user</span>
          </button>
          <div className="safety-menu-separator">
            <span className="danger-zone-label">Danger Zone</span>
          </div>
          <button
            className={`safety-menu-item ${blocked ? 'unblock' : 'danger'}`}
            onClick={handleBlock}
            disabled={blockLoading}
          >
            <FaBan />
            <span>{blocked ? 'Unblock user' : 'Block user'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserSafetyMenu;
